import { useSyncExternalStore } from "react";
import type { PostHog, PostHogConfig } from "posthog-js";
import type * as PostHogSdk from "posthog-js";

import {
  installRequestFailureReporting,
  resetRequestFailureReportingForTests,
} from "@client/telemetry/posthogRequestFailures";

type FeatureFlagValue = boolean | string;
type FeatureFlags = Record<string, FeatureFlagValue>;
type PostHogModule = typeof PostHogSdk;
type QueuedCall = (client: PostHog) => void;

const MAX_QUEUED_CALLS = 100;

let posthogClient: PostHog | null = null;
let initialization: Promise<void> | null = null;
let queuedCalls: QueuedCall[] = [];
let featureFlags: FeatureFlags = {};
let initialPageviewDeferred = false;
/** Super properties that describe the device/session and must survive an
 * identity reset (see registerBootProperties). */
let bootProperties: Record<string, unknown> = {};
let initialPageviewReleased = false;
const featureFlagListeners = new Set<() => void>();

function persistenceKey(apiKey: string): string {
  const escapedKey = apiKey
    .replace(/\+/g, "PL")
    .replace(/\//g, "SL")
    .replace(/=/g, "EQ");
  return `ph_${escapedKey}_posthog`;
}

function readPersistedFeatureFlags(apiKey: string): FeatureFlags {
  try {
    const persisted = window.localStorage.getItem(persistenceKey(apiKey));
    if (!persisted) return {};

    const parsed: unknown = JSON.parse(persisted);
    if (typeof parsed !== "object" || parsed === null) return {};

    const enabled = (parsed as Record<string, unknown>)[
      "$enabled_feature_flags"
    ];
    if (typeof enabled !== "object" || enabled === null) return {};

    return Object.fromEntries(
      Object.entries(enabled).filter(
        (entry): entry is [string, FeatureFlagValue] =>
          typeof entry[1] === "boolean" || typeof entry[1] === "string",
      ),
    );
  } catch {
    return {};
  }
}

function replaceFeatureFlags(next: FeatureFlags): void {
  featureFlags = next;
  notifyFeatureFlagListeners();
}

function notifyFeatureFlagListeners(): void {
  for (const listener of featureFlagListeners) listener();
}

function queueOrRun(call: QueuedCall): void {
  if (posthogClient) {
    call(posthogClient);
    return;
  }

  if (queuedCalls.length === MAX_QUEUED_CALLS) queuedCalls.shift();
  queuedCalls.push(call);
}

function configurePostHogClient(
  client: PostHog,
  apiKey: string,
  config: Partial<PostHogConfig>,
): void {
  // The SDK captures the initial `$pageview` inside `init`, which would put
  // it on the wire before the super properties the entry registers at boot
  // (app_surface, storage_backend, app_version…) — so the first event of
  // every session, and the only event of a short one, would lack them. When
  // the entry asks for automatic pageviews, keep the SPA history pageviews
  // automatic but hold the initial one until the entry calls
  // `releaseInitialPageview()` after its boot telemetry is registered. The
  // release goes through the same FIFO queue as the registrations, so the
  // order holds whether the SDK has finished loading or not, and whether the
  // entry initialized synchronously (native) or is racing a chunk (web).
  initialPageviewDeferred = config.capture_pageview === true;
  // `capture_pageview: false` for the SDK's own init: any truthy value —
  // "history_change" included — makes posthog-js schedule its own initial
  // pageview from `_loaded` (verified in posthog-js 1.372.10), which would
  // both precede the registrations and duplicate ours. SPA history pageviews
  // are switched on at release time via set_config, which restarts the
  // history extension without re-running the boot capture.
  client.init(
    apiKey,
    initialPageviewDeferred ? { ...config, capture_pageview: false } : config,
  );
  // Before the first capture can go out: a request the browser rejects is
  // otherwise invisible (see posthogRequestFailures.ts).
  installRequestFailureReporting(client);
  posthogClient = client;

  // Flush the calls queued while the SDK was loading BEFORE anything can
  // read through the live client: the feature-flag hooks re-read on notify
  // (a live read records an exposure event), and `onFeatureFlags` fires on
  // the first flag response. Both would otherwise emit ahead of the boot
  // registrations queued by the entry.
  const pending = queuedCalls;
  queuedCalls = [];
  for (const call of pending) call(client);

  // Hooks may already be rendering persisted values while the SDK chunk loads.
  // Re-read through the SDK now so it records the same exposure events as the
  // PostHog React hooks did before deferral.
  notifyFeatureFlagListeners();

  client.onFeatureFlags((flags, variants) => {
    replaceFeatureFlags(
      Object.fromEntries(flags.map((flag) => [flag, variants[flag] ?? true])),
    );
  });
}

/**
 * Test seam: forget the client, queue, flags and pageview state so a test can
 * initialize the wrapper against its own fake client regardless of what an
 * earlier test in the same process did. Never called by app code.
 */
export function resetPostHogForTests(): void {
  resetRequestFailureReportingForTests();
  posthogClient = null;
  initialization = null;
  queuedCalls = [];
  featureFlags = {};
  initialPageviewDeferred = false;
  initialPageviewReleased = false;
  bootProperties = {};
}

/**
 * Capture the session's initial `$pageview`, held back by initialization (see
 * configurePostHogClient). Entries call this once their boot super properties
 * are registered; idempotent, and a no-op when the entry did not ask for
 * automatic pageviews. Queued like every other call, so it lands after the
 * registrations queued before it even if the SDK is still loading.
 */
export function releaseInitialPageview(): void {
  if (initialPageviewReleased) return;
  initialPageviewReleased = true;
  queueOrRun((client) => {
    if (!initialPageviewDeferred) return;
    // From here on the SDK tracks SPA navigations itself; this only starts
    // the history listener, it never re-runs the boot-time initial capture.
    client.set_config({ capture_pageview: "history_change" });
    client.capture("$pageview");
  });
}

/**
 * Starts loading PostHog without making it part of the entry's static import
 * closure. Calls made while the SDK chunk is loading are replayed in order.
 */
/** The SDK with the extension it wants at `init` — see posthogSdkBundle.ts. */
function loadPostHogSdk(): Promise<PostHogModule> {
  return import("./posthogSdkBundle");
}

export function initializePostHog(
  apiKey: string,
  config: Partial<PostHogConfig>,
  load: () => Promise<PostHogModule> = loadPostHogSdk,
): Promise<void> {
  if (initialization) return initialization;

  replaceFeatureFlags(readPersistedFeatureFlags(apiKey));
  initialization = load()
    .then((module) => {
      configurePostHogClient(module.default, apiKey, config);
    })
    .catch((error: unknown) => {
      console.error("Failed to initialize PostHog", error);
    });

  return initialization;
}

/** Keeps the installed mobile bundle's local SDK initialization synchronous. */
export function initializePostHogSynchronously(
  apiKey: string,
  config: Partial<PostHogConfig>,
  client: PostHog,
): void {
  if (initialization) return;
  replaceFeatureFlags(readPersistedFeatureFlags(apiKey));
  configurePostHogClient(client, apiKey, config);
  initialization = Promise.resolve();
}

export function identifyPostHogUser(
  ...args: Parameters<PostHog["identify"]>
): void {
  queueOrRun((client) => client.identify(...args));
}

export function resetPostHog(): void {
  queueOrRun((client) => {
    client.reset();
    // `reset` clears persisted super properties along with the identity.
    // The boot properties describe the device and this launch, not the
    // person, so they must outlive the reset — otherwise the login screen
    // and the next anonymous session report with none of them.
    if (Object.keys(bootProperties).length > 0) {
      client.register({ ...bootProperties });
    }
  });
}

/**
 * Register super properties that describe the device and this launch
 * (storage backend, Lockdown signals, app surface, build) rather than the
 * person. They are re-registered after `resetPostHog()`, which otherwise
 * wipes every super property together with the identity.
 */
export function registerBootProperties(
  properties: Record<string, unknown>,
): void {
  bootProperties = { ...bootProperties, ...properties };
  registerPostHogProperties(properties);
}

export function registerPostHogProperties(
  ...args: Parameters<PostHog["register"]>
): void {
  queueOrRun((client) => client.register(...args));
}

export function capturePostHogEvent(
  ...args: Parameters<PostHog["capture"]>
): void {
  queueOrRun((client) => void client.capture(...args));
}

/**
 * Start recording, loading the bundled recorder (the SDK's "lazy-recorder"
 * extension, ~146 KB) first. The recorder is deliberately NOT part of the
 * SDK chunk: the entries start recording only once startup has settled
 * (see utils/posthogRecording.ts), so its download and evaluation belong
 * behind that same gate, off the startup path and never at all where
 * recording is never started (previews, staging). Should the chunk fail to
 * load, the SDK falls back to fetching the script itself, as it always did.
 */
export function startPostHogSessionRecording(): void {
  queueOrRun((client) => {
    void import("posthog-js/dist/lazy-recorder")
      .catch(() => undefined)
      .then(() => client.startSessionRecording());
  });
}

export function getPostHogSessionReplay(): {
  sessionId?: string;
  url?: string;
} {
  if (!posthogClient) return {};

  return {
    sessionId: posthogClient.get_session_id(),
    url: posthogClient.get_session_replay_url({ withTimestamp: true }),
  };
}

function subscribeToFeatureFlags(listener: () => void): () => void {
  featureFlagListeners.add(listener);
  return () => featureFlagListeners.delete(listener);
}

export function useFeatureFlagVariantKey(
  flag: string,
): FeatureFlagValue | undefined {
  return useSyncExternalStore(
    subscribeToFeatureFlags,
    () =>
      posthogClient ? posthogClient.getFeatureFlag(flag) : featureFlags[flag],
    () => undefined,
  );
}

/**
 * Non-React read of a feature flag, for boot-time gates that run before any
 * component mounts: the live client when initialized, else the snapshot
 * persisted by the previous session. `undefined` when the flag has never
 * been evaluated on this device (first run, or PostHog not initialized) —
 * callers choose their own default for that case.
 */
export function getFeatureFlagEnabled(
  flag: string,
  {
    recordExposure = true,
  }: {
    /**
     * A live read normally records a `$feature_flag_called` exposure event.
     * Pass `false` for boot-time gates that run before the session's boot
     * properties are registered: an exposure event emitted then would carry
     * the previous launch's persisted properties, and a startup gate is not
     * a product exposure anyway.
     */
    recordExposure?: boolean;
  } = {},
): boolean | undefined {
  if (posthogClient) {
    return posthogClient.isFeatureEnabled(flag, {
      send_event: recordExposure,
    });
  }
  const value = featureFlags[flag];
  return value === undefined ? undefined : Boolean(value);
}

export function useFeatureFlagEnabled(flag: string): boolean | undefined {
  return useSyncExternalStore(
    subscribeToFeatureFlags,
    () => {
      if (posthogClient) return posthogClient.isFeatureEnabled(flag);
      const value = featureFlags[flag];
      return value === undefined ? undefined : Boolean(value);
    },
    () => undefined,
  );
}
